import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';

import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import {
  useEntertainerDashboardQuery,
  useUserDashboardNotificationsQuery,
} from '@queries/UserQueries';

import { useCitiesQuery, useCityById, useCurrentUserCity } from '@hooks/useCurrentUserLocation';
import { usePWA } from '@hooks/usePWA';

import { useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { ReportOverlay } from '@components/overlays/ReportOverlay/ReportOverlay';

import { useInAppNotifications } from '@screens/NotificationsScreen/NotificationsScreen';
import { ChangeCityButton } from '@screens/SettingsScreen';

import { ConversationsIcon } from './CommunityScreen';
import './CommunityScreen.scss';
import { DashboardScreen } from './DashboardScreen';
import { DashboardPosterGrid } from './components/DashboardPosterGrid';
import { Featured } from './components/Featured';
import { SuggestedActions } from './components/SuggestedActions';
import { TipJarSection } from './components/TipJarSection';
import { WeeklyMetrics } from './components/WeeklyMetrics';
import { WeeklyScore } from './components/WeeklyScore';

export const EntertainerDashboardScreen = createScreenComponent<{ cityId?: string }>(
  ({ f7route, cityId: cityOverrideId }) => {
    const { navigate } = useRouter();
    const { entertainer } = useCurrentEntertainerQuery();
    const { hasUnreadNotifications } = useInAppNotifications();
    const { currentCityIsReady, currentCity: calculatedCurrentCity } = useCurrentUserCity({
      forceFetch: true,
    });
    const { currentCityOverride } = useAppContext();
    const [supportOverlayIsVisible, setSupportOverlayIsVisible] = useState(false);

    const cityId = cityOverrideId ?? currentCityOverride?._id ?? calculatedCurrentCity?._id ?? '';
    const { city: currentCity } = useCityById(cityId);

    const pwaPromptRef = useRef(false);
    const { canInstallPWA, promptPWAInstall } = usePWA();

    useEffect(() => {
      if (f7route.query?.installPWA === 'true' && canInstallPWA && !pwaPromptRef.current) {
        pwaPromptRef.current = true;
        promptPWAInstall()
          .then(() => {
            pwaPromptRef.current = false;
          })
          .catch(() => {
            pwaPromptRef.current = false;
          });
      }
    }, []);

    const { refetchEntertainerDashboard, entertainerDashboard, entertainerDashboardIsReady } =
      useEntertainerDashboardQuery({
        cityId: currentCity?._id ?? '',
      });
    const { refetchUserDashboardNotifications } = useUserDashboardNotificationsQuery({
      cityId: currentCity?._id ?? '',
    });

    const handleReenter = () => {
      refetchEntertainerDashboard();
      refetchUserDashboardNotifications();
    };

    const title = useMemo(() => {
      if (!entertainer) {
        return <Skeleton height={28} />;
      }

      const rootMessage = `Hey ${entertainer?.title}!`;

      try {
        const isKing = entertainer?.details?.categories?.includes('Drag King');
        const isThing = entertainer?.details?.categories?.includes('Drag Thing');

        if (isThing) {
          return `${rootMessage} 👹`;
        }

        if (isKing) {
          return `${rootMessage} 👑`;
        }
      } catch (e) {
        Sentry.captureException(e);
      }

      return `${rootMessage} 💃🏽`;
    }, [entertainer]);

    return (
      <Screen
        name="Entertainer Dashboard Screen"
        disableLocationPermissionGate={false}
        onReenter={handleReenter}
        headerProps={{
          left: (
            <div className="ps-2 d-flex align-items-center" style={{ gap: 10 }}>
              <Screen.GagglTitle />
              <ChangeCityButton
                buttonProps={{
                  variant: 'smooth',
                  style: { padding: '4px 12px' },
                }}
              />
            </div>
          ),
          right: (
            <div className="d-flex gap-2">
              <ConversationsIcon />
              <IconButton
                color="neutral"
                variant="smooth"
                badge={hasUnreadNotifications}
                onClick={() => {
                  navigate('/notifications');
                }}
              >
                <FontAwesomeIcon name="bell" />
              </IconButton>
            </div>
          ),
        }}
      >
        <Screen.Content maxWidth={425}>
          <TitleToolbar
            text={<span style={{ fontSize: '1.5rem' }}>{title}</span>}
            className="title-toolbar-welcome mb-0"
            size="md"
          />
          {currentCity?.name ? (
            <p className="mt-0">{`Dive into the ${currentCity?.name} community`}</p>
          ) : (
            <div className="w-100 mb-2">
              <Skeleton height={26} />
            </div>
          )}
          <div className="mb-3">
            <WeeklyScore
              isAppDashboardReady={entertainerDashboardIsReady}
              appDashboard={entertainerDashboard}
            />
          </div>
          <WeeklyMetrics
            isAppDashboardReady={entertainerDashboardIsReady}
            appDashboard={entertainerDashboard}
          />
          <SuggestedActions
            isAppDashboardReady={entertainerDashboardIsReady}
            appDashboard={entertainerDashboard}
            onOpenSupport={() => setSupportOverlayIsVisible(true)}
          />
          <Featured
            isAppDashboardReady={entertainerDashboardIsReady}
            appDashboard={entertainerDashboard}
          />
          <DashboardPosterGrid
            isAppDashboardReady={entertainerDashboardIsReady}
            appDashboard={entertainerDashboard}
          />
        </Screen.Content>

        <ReportOverlay
          isVisible={supportOverlayIsVisible}
          title="Feedback & Support"
          content={
            <p>
              We're still in beta so we are VERY open to feedback. If you have a question or there's
              a bug that we missed. Please let us know so that we can fix it!
            </p>
          }
          submitButtonText="Send"
          data={{
            collectionName: 'support',
            objectId: entertainer?.userId,
          }}
          onClose={() => setSupportOverlayIsVisible(false)}
        />
      </Screen>
    );
  },
);
